import { toValue, type MaybeRefOrGetter } from 'vue';
import {
  classifyContextMenuTarget,
  contextMenuBuilders,
  type MenuActions,
} from './contextMenuBuilders';
import type { FolderLikeItem } from '../../types';
import { useFileManagerActionVisibility } from '../../permissions/useFileManagerActionVisibility';
import { getParentPath } from '../../utils';
import { useFileManagerCopy } from './useFileManagerCopy';
import { openContextMenuIfAny, useFileManagerContextMenu } from './useFileManagerContextMenu';
import { useFileManagerCreateFolder } from './useFileManagerCreateFolder';
import { useFileManagerDelete } from './useFileManagerDelete';
import { useFileManagerRename } from './useFileManagerRename';
import { useFileManagerTools } from '../tools/useFileManagerTools';
import { useFileManagerUpload } from '../upload/useFileManagerUpload';

// The sidebar tree only ever has one implicit "selection" (currentPath, driven by navigation) and
// no ctrl/shift multi-select, so unlike useFileGridInteractions's onItemContextMenu this never needs
// resolveContextMenuTarget — the target is always just the clicked row. It DOES still classify it,
// because the tree's top level is now the mount table's own rows: a mount (or, in a multi-project
// table, a namespace level above one) must get the 'synthetic' builder — no rename, no delete, since
// neither is a driveItem — rather than the 'single-folder' one that used to be the only builder able
// to apply here. Deliberately does NOT
// call selectOnly/navigateTo: right-clicking a tree row opens the menu without touching what's
// currently navigated/expanded.
export const useDirectoryTreeContextMenu = (item: MaybeRefOrGetter<FolderLikeItem>) => {
  const contextMenu = useFileManagerContextMenu();
  const { renamingPaths, canRenameAt, requestRename } = useFileManagerRename();
  const { deletingPaths, canDeleteAt, requestDelete } = useFileManagerDelete();
  const { copyingPaths, canCopyAt, requestCopy } = useFileManagerCopy();
  const createFolderState = useFileManagerCreateFolder();
  const uploadState = useFileManagerUpload();

  // Same visibility-mode gating useFileGridInteractions uses for the grid's own folder menu — this
  // goes through the exact same classify/dispatch and the same builders, so both surfaces stay in
  // sync automatically.
  const { showCreateFolderAt, showCreateFileAt } = useFileManagerActionVisibility();
  // Usable here even though this composable never injects useFileManagerPermissions(): useFileManagerTools
  // reads permissions itself, so adopting tools needed no new plumbing.
  const { toolsFor } = useFileManagerTools();

  const onContextMenu = (event: MouseEvent) => {
    const target = [toValue(item)];
    const targetPath = target[0].path;
    // Passed unconditionally, exactly as every other slot is. The tree only ever targets folder-shaped
    // rows, so isAvailable resolves this to nothing today — and a folder-shaped tool later needs no edit
    // here.
    const tools = toolsFor(target);
    const actions: MenuActions = {
      createFolder:
        showCreateFolderAt(targetPath) && createFolderState.canCreateFolderAt(targetPath)
          ? { request: () => createFolderState.openModal(targetPath) }
          : undefined,
      createFile:
        showCreateFileAt(targetPath) && uploadState.canUploadTo(targetPath)
          ? { request: () => uploadState.openFilePicker(targetPath) }
          : undefined,
      rename:
        canRenameAt(targetPath) && !renamingPaths.value.has(targetPath)
          ? { request: () => requestRename(target[0]) }
          : undefined,
      // Asked about the PARENT, since a copy creates its duplicate beside the target rather than
      // inside it — which also means a mount row's gate asks about the virtual root and fails, on top
      // of buildSyntheticMenu ignoring the slot anyway.
      copy:
        canCopyAt(getParentPath(targetPath)) && !copyingPaths.value.has(targetPath)
          ? { request: () => requestCopy(target[0]) }
          : undefined,
      tools: tools.length ? { items: tools } : undefined,
      delete: canDeleteAt(targetPath)
        ? { request: () => requestDelete(target) }
        : undefined,
    };
    const menuItems = contextMenuBuilders[classifyContextMenuTarget(target)](target, actions);
    const opened = openContextMenuIfAny(
      { x: event.clientX, y: event.clientY },
      menuItems,
      contextMenu,
    );
    if (opened) event.preventDefault();
  };

  return { onContextMenu, renamingPaths, deletingPaths, copyingPaths };
};
